/**
 * Custom hook for managing game tooltip state and Phaser integration.
 * Extracts tooltip logic from App.tsx to keep it clean.
 */

import { useState } from 'react';
import { TilePosition } from '@shared/utils/coordinates';
import { TerrainType } from '@shared/constants/terrain';

export interface TooltipState {
  tile: TilePosition | null;
  terrainType: TerrainType | null;
  isMineNode: boolean;
  position: { x: number; y: number };
  visible: boolean;
}

/**
 * Hook for managing game tooltip state.
 */
export function useGameTooltip(gameContainerRef?: React.RefObject<HTMLDivElement>) {
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    tile: null,
    terrainType: null,
    isMineNode: false,
    position: { x: 0, y: 0 },
    visible: false,
  });
  const [hoveredTile, setHoveredTile] = useState<TilePosition | null>(null);

  /**
   * Set up tooltip callback for a Phaser scene.
   */
  const setupTooltipCallback = (gameScene: unknown) => {
    // Type guard for TilemapScene
    if (gameScene && typeof gameScene === 'object' && gameScene !== null) {
      const sceneWithMethods = gameScene as {
        getTileInteractionController?: () => {
          setOnHoverChange?: (callback: (tile: TilePosition | null) => void) => void;
        } | null;
        getMapData?: () => {
          getTerrain: (x: number, y: number) => TerrainType;
          isMineNodePosition: (x: number, y: number) => boolean;
        } | null;
        input?: {
          activePointer: {
            x: number;
            y: number;
            worldX: number;
            worldY: number;
          };
        };
        sys?: {
          game?: {
            canvas?: HTMLCanvasElement;
          };
        };
      };

      const interactionController = sceneWithMethods.getTileInteractionController?.();
      if (interactionController?.setOnHoverChange) {
        interactionController.setOnHoverChange((tile) => {
          if (tile && sceneWithMethods.getMapData) {
            const mapData = sceneWithMethods.getMapData();
            if (mapData && sceneWithMethods.input) {
              const terrainType = mapData.getTerrain(tile.x, tile.y);
              const isMineNode = mapData.isMineNodePosition(tile.x, tile.y);
              // Get mouse position for tooltip placement
              // In Phaser, pointer.x and pointer.y are screen coordinates relative to the canvas
              // pointer.worldX and pointer.worldY are world coordinates (not what we want)
              // We need to convert canvas coordinates to container coordinates
              const pointer = sceneWithMethods.input.activePointer;
              const container = gameContainerRef?.current;
              let tooltipX = pointer.x;
              let tooltipY = pointer.y;

              if (container) {
                const canvas = container.querySelector('canvas');
                if (canvas) {
                  // Get bounding rects to calculate offset
                  const canvasRect = canvas.getBoundingClientRect();
                  const containerRect = container.getBoundingClientRect();
                  // Calculate position relative to container (accounting for canvas offset within container)
                  tooltipX = pointer.x + (canvasRect.left - containerRect.left);
                  tooltipY = pointer.y + (canvasRect.top - containerRect.top);
                } else {
                  // Fallback: if no canvas found, assume canvas is at (0,0) in container
                  // pointer.x and pointer.y should already be relative to container
                  tooltipX = pointer.x;
                  tooltipY = pointer.y;
                }
              }

              setHoveredTile(tile);
              setTooltipState({
                tile,
                terrainType,
                isMineNode,
                position: { x: tooltipX, y: tooltipY },
                visible: true,
              });
            }
          } else {
            setHoveredTile(null);
            setTooltipState((prev) => ({ ...prev, visible: false }));
          }
        });
      }
    }
  };

  return {
    tooltipState,
    hoveredTile,
    setupTooltipCallback,
  };
}
